import ELRMileAndChain from "components/util/ELRMileAndChain";
import OBCSpinner from "components/util/OBC";
import _ from "lodash";
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FixedSizeList } from "react-window";
import { getComparableSessions, setSecondaryManualOffset, setSecondarySessionData, toggleCompareSessionsDraggable } from "redux/actions";
import DraggableWrapper from "../DraggableWrapper";
import { INDEX_TOLERANCE } from "../RailInspectUtils";
import RailCompareDraggableListItem from "./RailCompareDraggableListItem";

const openSelector = (state) => state.railInspection.sessionHistoryDraggable;
const comparableSessionsSelector = (state) => state.railInspection.comparableSessions;
const railDataObjectSelector = (state) => state.railInspection.railInspectionImages.data;
const railDataLoadedSelector = (state) =>
    state.railInspection.railInspectionImages.loadingInfo.percentageComplete > 0 &&
    state.railInspection.railInspectionImages.loadingInfo.sessionID === state.playlist.data.routeID;
const selectedRailImageIndexSelector = (state) => state.railInspection.selectedRailInspectionImage.index;
const viewsSelector = (state) => state.views;
const dashboardsSelector = (state) => state.dashboards;
const userDetailsSelector = (state) => state.userDetails;
const elrUnitsSelector = (state) => state.userDetails.userConfig.elr_units;
const reduxSessionIDSelector = (state) => state.playlist.data.routeID;

// @ts-ignore
const RailCompareDraggable = memo(({ windowDimensions, loadSecondarySession, clearSecondarySession }) => {
    const dispatch = useDispatch();

    const open = useSelector(openSelector);
    /**
     * @type {number[]}
     */
    const comparableSessions = useSelector(comparableSessionsSelector);

    const railDataObject = useSelector(railDataObjectSelector);
    const railDataLoaded = useSelector(railDataLoadedSelector);
    const selectedRailImageIndex = useSelector(selectedRailImageIndexSelector);
    const views = useSelector(viewsSelector);
    const dashboards = useSelector(dashboardsSelector);
    const userDetails = useSelector(userDetailsSelector);
    const dashboardID = userDetails.dashboardAccessID;
    const userViewOffsets = _.get(views, [userDetails.userConfig.view_id, "datum_offsets"], []);
    const currentDashboard = _.find(dashboards, (dash) => dash.access_id === dashboardID);
    const workspaceViewOffsets = _.get(views, [_.get(currentDashboard, ["config", "view_id"], -1), "datum_offsets"], []);
    const datumOffsets = workspaceViewOffsets.length ? workspaceViewOffsets : userViewOffsets;
    const elrUnits = useSelector(elrUnitsSelector);
    const primaryID = useSelector(reduxSessionIDSelector);
    const [lastAutoAlignedIndex, setLastAutoAlignedIndex] = useState(null);
    const [loaded, setLoaded] = useState(false);

    //might be worth uplifting this to redux
    const [selectedSession, setSelectedSession] = useState(null);

    const railImages = railDataObject && railDataLoaded ? railDataObject : [];
    const railImage = railImages && railImages.length ? railImages[selectedRailImageIndex] : null;
    const selectedItemRef = useRef(null);

    //add auto align to redux and pass in secondaryRailImageStatus loaded and call it from here

    //timestamps aren't valid for identification as the timestamp in the key is not unique to the session...

    //will use new mwv fall back in inspection image packs
    const mwvData = useMemo(() => {
        if (!railImages || !railImage) return null;

        let mwv = railImage["mwv"];

        if (!mwv || !mwv.elr || !mwv.trid) return null;

        mwv = ELRMileAndChain.from_fields(
            "ELR Mile & Chain",
            "elr_mile_yards",
            {
                ELR: mwv.elr,
                MILE: mwv.mile,
                TRACK: mwv.trid,
                YARDS: mwv.yard,
            },
            datumOffsets,
            true,
        );

        return {
            string: mwv.to_string(elrUnits, datumOffsets, true),
            route: mwv.route,
            track: mwv.track,
            position: mwv.position,
        };
    }, [railImage, railImages, datumOffsets, elrUnits]);

    const filteredIds = useMemo(() => {
        if (!comparableSessions || !primaryID) {
            return [];
        }

        const filteredSessions = comparableSessions.filter((id) => id !== primaryID);

        if (selectedSession && !filteredSessions.includes(selectedSession)) filteredSessions.push(selectedSession);

        return filteredSessions.sort((a, b) => b - a);
    }, [comparableSessions, primaryID, selectedSession]);

    useEffect(() => {
        if (!open || !mwvData || (lastAutoAlignedIndex && Math.abs(lastAutoAlignedIndex - selectedRailImageIndex) < INDEX_TOLERANCE)) return;

        dispatch(
            getComparableSessions(mwvData.route, mwvData.track, mwvData.position, () => {
                setLoaded(true);
            }),
        );
        setLastAutoAlignedIndex(selectedRailImageIndex);
    }, [open, dispatch, mwvData, lastAutoAlignedIndex, selectedRailImageIndex]);

    const handleClick = useCallback(
        (sessionID) => {
            setSelectedSession(sessionID);
            loadSecondarySession(sessionID);
            dispatch(setSecondaryManualOffset(0));
        },
        [dispatch, loadSecondarySession],
    );

    const handleClear = useCallback(() => {
        setSelectedSession(null);
        clearSecondarySession(false);
        dispatch(setSecondarySessionData(null));
    }, [clearSecondarySession, dispatch]);

    useEffect(() => {
        if (!selectedItemRef.current) return;

        selectedItemRef.current.scrollIntoView({
            behavior: "smooth",
            block: "center",
        });
    }, [selectedSession, comparableSessions]);

    const Row = useCallback(
        ({ index, style }) => {
            const sessionId = filteredIds[index];

            return (
                <RailCompareDraggableListItem
                    key={sessionId}
                    sessionId={sessionId}
                    comparableSessions={comparableSessions}
                    selectedSession={selectedSession}
                    selectedItemRef={selectedItemRef}
                    handleClick={handleClick}
                    style={{
                        ...style,
                        left: style.left,
                        top: index === 0 ? style.top : style.top + 8,
                        width: style.width,
                        height: index === 0 ? style.height : style.height - 8,
                    }}
                />
            );
        },
        [comparableSessions, filteredIds, handleClick, selectedSession],
    );

    const content = useMemo(() => {
        if (!loaded)
            return (
                <div>
                    {/* @ts-ignore*/}
                    <OBCSpinner
                        colorScheme="mono"
                        size={60}
                    />
                </div>
            );

        return (
            <div
                className="w-full flex flex-col overflow-hidden"
                style={{ gap: "8px" }}>
                <div className="h-full flex flex-1 flex-col overflow-hidden relative">
                    <FixedSizeList
                        height={400}
                        width={300}
                        itemSize={70}
                        itemKey={(index) => filteredIds[index]}
                        itemCount={filteredIds.length}>
                        {Row}
                    </FixedSizeList>
                </div>
                <div
                    className="flex w-full"
                    style={{ marginBottom: "8px" }}>
                    <button
                        style={{ marginTop: "8px" }}
                        className="inspection-default-button flex justify-center"
                        onClick={handleClear}>
                        Close Secondary Session
                    </button>
                </div>
            </div>
        );
    }, [Row, filteredIds, handleClear, loaded]);

    const closeAction = () => {
        dispatch(toggleCompareSessionsDraggable(false));
    };

    if (!open) return null;

    return (
        <DraggableWrapper
            id="RailCompareSessionsDraggable"
            content={content}
            title={
                <div
                    className="flex"
                    style={{ gap: 8, marginRight: 16, minWidth: 156 }}>
                    <span className="font-bold">Inspection History</span>
                </div>
            }
            windowDimensions={windowDimensions}
            closeAction={closeAction}
            customDimensions={{ x: "300", y: "auto" }}
            customLocation={{ x: 0, y: 0 }}
        />
    );
});

export default RailCompareDraggable;
